import { usePage } from "@inertiajs/react"
import { ClipboardList, MapPin } from "lucide-react"
import { UserLayout } from "@/components/layouts/user-layout"
import { ScreenHeader } from "@/components/ui/headers"
import { MetaChip, OrderLine } from "@/features/user/order-line"
import type { OrderTracking } from "@/features/user/types"
import { t } from "@/lib/i18n"

type OrderListItem = Pick<
  OrderTracking,
  | "id"
  | "name"
  | "status"
  | "statusLabel"
  | "serviceTypeLabel"
  | "quantity"
  | "lineTotal"
  | "currency"
  | "createdAt"
  | "tableName"
>

type Props = {
  orders: OrderListItem[]
}

export default function UserOrdersIndex() {
  const { orders } = usePage<Props>().props

  return (
    <UserLayout>
      <div className="text-foreground" data-testid="orders-index-view">
        <ScreenHeader
          title={t("ui.react.pages.user.orders.index.orders")}
          subtitle={t("ui.react.pages.user.orders.index.this_visit")}
        />
        {orders.length === 0 ? (
          <EmptyState />
        ) : (
          // Same rows as the bill: the history and the bill are two views of the
          // same orders, so they read the same way.
          <ul className="flex flex-col px-4 pb-6 pt-2" data-testid="orders-list">
            {orders.map((order) => (
              <OrderLine
                key={order.id}
                testId={`order-list-item-${order.id}`}
                href={`/orders/${order.id}`}
                quantity={order.quantity}
                name={order.name}
                status={order.status}
                statusLabel={order.statusLabel}
                lineTotal={order.lineTotal}
                currency={order.currency}
                chips={
                  <>
                    <MetaChip>{order.serviceTypeLabel}</MetaChip>
                    {order.tableName && (
                      <MetaChip>
                        <MapPin className="size-3" />
                        {order.tableName}
                      </MetaChip>
                    )}
                  </>
                }
              />
            ))}
          </ul>
        )}
      </div>
    </UserLayout>
  )
}

function EmptyState() {
  return (
    <div
      data-testid="orders-list-empty"
      className="flex flex-col items-center gap-3 px-6 py-16 text-center text-muted-foreground"
    >
      <span className="flex size-14 items-center justify-center rounded-full bg-muted">
        <ClipboardList className="size-6" />
      </span>
      <p className="text-sm font-medium">
        {t("ui.react.pages.user.orders.index.not_yet_no_have_done")}
      </p>
      <p className="max-w-64 text-xs">
        {t("ui.react.pages.user.orders.index.when_add_dishes_bill_appear")}
      </p>
    </div>
  )
}
